import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, concatMap, distinctUntilChanged, scan, switchMap } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';

import { SelectTeamMembersService } from '@core/tellent-members-logic';
import { TellentMember } from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import { injectUntilDestroyed } from '@core/utils-legacy';

@Injectable()
export class SelectTeamMembersController {
  private readonly takeUntilDestroyed = injectUntilDestroyed();
  private readonly itemsPerPage = 15;
  private readonly selectTeamMembersService = inject(SelectTeamMembersService);
  private readonly pagination = this.selectTeamMembersService.selectTeamMembersPagination;

  private readonly _selectedIds = new Set<string>();
  public readonly selectedIdsSize = signal(0);
  public readonly selectedIds = this._selectedIds;
  public readonly page$ = new BehaviorSubject<number>(1);
  public readonly query$ = new BehaviorSubject<string>('');
  public readonly isLoading = signal(false);

  public readonly items = toSignal(
    this.query$.pipe(
      distinctUntilChanged(),
      switchMap(query =>
        this.page$.pipe(
          debounceTime(1),
          distinctUntilChanged(),
          concatMap(page =>
            this.selectTeamMembersService.fetchAndSelectWithPageAndQuery(
              page,
              query,
              this.itemsPerPage,
            ),
          ),
          scan((acc, data) => [...acc, ...data], <TellentMember[]>[]),
        ),
      ),
      map(members =>
        members.map(member => ({
          ...member,
          fullName: resolveMemberFullName({
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
          }),
        })),
      ),
      tap(() => this.isLoading.set(false)),
      this.takeUntilDestroyed,
    ),
  );

  public readonly totalCount = computed(() => this.pagination()?.totalCount);

  public toggleAssignment(admin: TellentMember, isDisabled: boolean, event: Event): void {
    if (isDisabled) {
      event.stopImmediatePropagation();
      return;
    }
    this._selectedIds.has(admin.guid)
      ? this._selectedIds.delete(admin.guid)
      : this._selectedIds.add(admin.guid);
    this.selectedIdsSize.set(this._selectedIds.size);
  }

  public fetchMore(event: IPageInfo) {
    if (
      this.isLoading() ||
      !this.totalCount() ||
      event.endIndex !== this.items()!.length! - 1 ||
      this.page$.value * this.itemsPerPage >= this.totalCount()!
    )
      return;
    this.page$.next(this.page$.value + 1);
  }

  public setQuery(query: string) {
    this.page$.next(1);
    this.query$.next(query);
  }
}
